/**
 * Serialização de filtros + paginação para URLSearchParams (E13.F5.T01/T04).
 *
 * Mantido em arquivo separado para deixar `CasosPage.tsx` focado em
 * orquestração e abaixo do limite eslint de 400 linhas. Funções puras.
 */
import { ptBR } from '@/i18n/pt-BR';
import { dataCivilEmUtc } from '@/lib/data_civil';
import type { CaseStatus as UIStatus } from '@/components/ui/StatusPill';
import type { CaseRecordData, CaseStatus } from '@/lib/cases';
import { EMPTY_FILTERS, STATUS_VALUES, type CasosFilterState } from '@/components/Casos/filters';
import {
  DEFAULT_PAGE_SIZE,
  isValidPageSize,
  type PageSize,
} from '@/components/Casos/CasosPagination';

const UPDATED_AT_FMT = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: '2-digit',
});

// E176.F1.C01 (GUS-252 item 1): o prazo é data civil — formatado em UTC, como foi lido.
const DEADLINE_FMT = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  timeZone: 'UTC',
});

export function parseStatusesParam(value: string | null): readonly CaseStatus[] {
  if (value === null || value === '') return [];
  const raw = value.split(',').map((s) => s.trim());
  const valid: CaseStatus[] = [];
  for (const candidate of raw) {
    if (STATUS_VALUES.includes(candidate as CaseStatus)) {
      valid.push(candidate as CaseStatus);
    }
  }
  return valid;
}

export function filtersFromSearchParams(params: URLSearchParams): CasosFilterState {
  return {
    ...EMPTY_FILTERS,
    cpf: params.get('cpf') ?? '',
    contrato: params.get('contrato') ?? '',
    processo: params.get('processo') ?? '',
    statuses: parseStatusesParam(params.get('status')),
    especime: params.get('especime') ?? '',
    dateFrom: params.get('dateFrom') ?? '',
    dateTo: params.get('dateTo') ?? '',
    awaitingMyReview: params.get('awaitingMyReview') === '1',
    leituraEmConferencia: params.get('leitura') === '1',
  };
}

export function searchParamsFromFilters(
  filters: CasosFilterState,
  page: number,
  size: PageSize,
): URLSearchParams {
  const out = new URLSearchParams();
  // Campos de texto: data-driven para manter a complexidade ciclomática baixa.
  const textFields: ReadonlyArray<[string, string]> = [
    ['cpf', filters.cpf],
    ['contrato', filters.contrato],
    ['processo', filters.processo],
    ['especime', filters.especime],
    ['dateFrom', filters.dateFrom],
    ['dateTo', filters.dateTo],
  ];
  for (const [key, value] of textFields) {
    if (value !== '') out.set(key, value);
  }
  if (filters.statuses.length > 0) out.set('status', filters.statuses.join(','));
  if (filters.awaitingMyReview) out.set('awaitingMyReview', '1');
  if (filters.leituraEmConferencia) out.set('leitura', '1');
  if (page > 1) out.set('page', String(page));
  if (size !== DEFAULT_PAGE_SIZE) out.set('size', String(size));
  return out;
}

export function pageFromSearchParams(params: URLSearchParams): number {
  const raw = Number(params.get('page') ?? '1');
  if (!Number.isFinite(raw) || raw < 1) return 1;
  return Math.floor(raw);
}

export function sizeFromSearchParams(params: URLSearchParams): PageSize {
  const raw = Number(params.get('size') ?? String(DEFAULT_PAGE_SIZE));
  if (isValidPageSize(raw)) return raw;
  return DEFAULT_PAGE_SIZE;
}

export function statusLabelOf(
  status: CaseRecordData['status'],
  laudoDownloadedAt?: string | null,
): string {
  if (status === 'draft') return ptBR.casos.statusDraft;
  if (status === 'analysing') return ptBR.casos.statusAnalysing;
  if (status === 'done') {
    if (laudoDownloadedAt != null) return ptBR.casos.statusFinalizado;
    return ptBR.casos.statusDone;
  }
  return ptBR.casos.statusConflict;
}

// E23.F24: a escada de status vem do backend (workflow_status); valores batem
// 1:1 com o StatusPill. Fallback para o status básico do caso quando ausente.
const WORKFLOW_TO_UI: Record<NonNullable<CaseRecordData['workflow_status']>, UIStatus> = {
  rascunho: 'rascunho',
  em_analise: 'em_analise',
  encerrado_triagem: 'encerrado_triagem',
  // E72.F2.T00 — desfecho com Relatório Preliminar (E72.F1). Rótulo próprio: é
  // ele que diz ao usuário que existe documento para baixar.
  encerrado_com_relatorio: 'encerrado_com_relatorio',
  laudo_em_elaboracao: 'laudo_em_elaboracao',
  eletronica_concluida: 'eletronica_concluida',
  aguardando_arquivos: 'aguardando_arquivos',
  aguardando_revisor: 'aguardando_revisor',
  devolvido_pelo_revisor: 'devolvido_pelo_revisor',
  finalizado: 'finalizado',
  // E224.F1 (owner, 05/11): o caso assinado aparecia "Em análise".
  assinado: 'assinado',
  // E224.F2: a equipe cancelou o caso.
  cancelado: 'cancelado',
};

export function derivedUiStatus(c: CaseRecordData): UIStatus {
  // E72.F2.T00: um `workflow_status` que este bundle não conhece (backend novo,
  // aba aberta há horas) não pode derrubar a lista — cai no neutro, que tem
  // rótulo e pill próprios. O código cru NUNCA vai para a tela.
  if (c.workflow_status) return WORKFLOW_TO_UI[c.workflow_status] ?? 'indefinido';
  if (c.status === 'done' && c.laudo_downloaded_at != null) return 'finalizado';
  const map: Record<CaseRecordData['status'], UIStatus> = {
    draft: 'rascunho',
    analysing: 'em_analise',
    done: 'concluido',
    conflict: 'aguardando_revisao',
  };
  return map[c.status];
}

export function uiStatusLabel(ui: UIStatus): string {
  const t = ptBR.casos;
  const map: Record<UIStatus, string> = {
    rascunho: t.statusDraft,
    em_analise: t.statusAnalysing,
    encerrado_triagem: t.statusEncerradoTriagem,
    encerrado_com_relatorio: t.statusEncerradoComRelatorio,
    indefinido: t.statusIndefinido,
    laudo_em_elaboracao: t.statusLaudoEmElaboracao,
    eletronica_concluida: t.statusEletronicaConcluida,
    aguardando_arquivos: t.statusAguardandoArquivos,
    aguardando_revisao: t.statusAguardandoRevisor,
    aguardando_revisor: t.statusAguardandoRevisor,
    devolvido_pelo_revisor: t.statusDevolvidoRevisor,
    concluido: t.statusDone,
    finalizado: t.statusFinalizado,
    arquivado: t.statusDone,
    assinado: t.statusAssinado,
    cancelado: t.statusCancelado,
  };
  return map[ui];
}

/**
 * E209.F2.T03 (GUS-296 item 5f): a Situação na lista. O caso fica `draft` até a primeira verificação;
 * com a preparação pedida (em curso ou com falha), ele já foi enviado e não é mais "Rascunho" — a marca
 * da preparação ao lado diz em que ponto está.
 */
export function situacaoNaLista(
  c: CaseRecordData,
  comPreparacao: boolean,
): { status: UIStatus; label: string } {
  const status = derivedUiStatus(c);
  if (status === 'rascunho' && comPreparacao) {
    return { status: 'em_analise', label: ptBR.casos.statusEnviado };
  }
  return { status, label: uiStatusLabel(status) };
}

export function formatDeadline(raw: string | null): string {
  if (raw === null) return ptBR.casos.semData;
  return DEADLINE_FMT.format(dataCivilEmUtc(raw));
}

export function formatUpdatedAt(raw: string): string {
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return ptBR.casos.placeholderEmDash;
  return UPDATED_AT_FMT.format(date);
}
